import jsPDF from 'jspdf';
import {CellHook, CellHookData, VAlignType} from 'jspdf-autotable';
import {PDF_FONT_FAMILY, PDF_FONT_MAIN_COLOR} from './export-pdf-font';

type RichTextLine = {
  text: string;
  fontStyle: 'bold' | 'normal';
};

export type RichTextHooks = {
  didParseCell: CellHook;
  willDrawCell: CellHook;
  didDrawCell: CellHook;
};

type CreateAutoTableRichTextHooksConfig = {
  pdf: jsPDF;
  fontFamily?: string;
  textColor?: string;
  shouldProcess?: (data: CellHookData) => boolean;
};

export function createAutoTableRichTextHooks({
  pdf,
  fontFamily = PDF_FONT_FAMILY,
  textColor = PDF_FONT_MAIN_COLOR,
  shouldProcess,
}: CreateAutoTableRichTextHooksConfig): RichTextHooks {
  const shouldRenderRichText = (data: CellHookData): boolean => {
    if (data.section !== 'body') {
      return false;
    }
    return shouldProcess ? shouldProcess(data) : true;
  };

  return {
    didParseCell: function (data: CellHookData) {
      if (!shouldRenderRichText(data)) return;
      const raw = getRichTextRaw(data);
      if (!raw) return;

      const paddingLeft = data.cell.padding('left');
      const paddingRight = data.cell.padding('right');
      const cellWidth =
        typeof data.cell.styles.cellWidth === 'number'
          ? data.cell.styles.cellWidth
          : data.cell.width || data.column.width;
      const maxWidth = cellWidth > 0 ? cellWidth - paddingLeft - paddingRight - 0.5 : 0;
      const plainTextLines = getWrappedPlainTextLines(raw, maxWidth, pdf, fontFamily);

      data.cell.text = plainTextLines.length > 0 ? plainTextLines : [''];
    },
    willDrawCell: function (data: CellHookData) {
      if (!shouldRenderRichText(data)) return;
      if (!getRichTextRaw(data)) return;

      data.cell.text = [];
    },
    didDrawCell: function (data: CellHookData) {
      if (!shouldRenderRichText(data)) return;
      const raw = getRichTextRaw(data);
      if (!raw) return;

      const cell = data.cell;
      const fontSize = cell.styles.fontSize;
      const fontSizeMm = fontSize / pdf.internal.scaleFactor;
      const lineHeightFactor = pdf.getLineHeightFactor();
      const lineHeightMm = fontSizeMm * lineHeightFactor;
      const paddingLeft = cell.padding('left');
      const paddingRight = cell.padding('right');
      const paddingTop = cell.padding('top');
      const paddingBottom = cell.padding('bottom');
      const maxWidth = cell.width - paddingLeft - paddingRight - 0.5;
      const startX = cell.x + paddingLeft;
      const renderedLines = buildWrappedRichTextLines(raw, maxWidth, pdf, fontFamily);
      const contentHeight = renderedLines.length * lineHeightMm;
      const availableHeight = Math.max(cell.height - paddingTop - paddingBottom, 0);
      const verticalOffset = getVerticalOffset(cell.styles.valign, availableHeight, contentHeight);
      let currentY = cell.y + paddingTop + verticalOffset;

      pdf.setFontSize(fontSize);
      pdf.setTextColor(textColor);

      for (const line of renderedLines) {
        pdf.setFont(fontFamily, line.fontStyle);
        if (line.text) {
          pdf.text(line.text, startX, currentY, {baseline: 'top'});
        }
        currentY += lineHeightMm;
      }

      pdf.setFont(fontFamily, 'normal');
    },
  };
}

function hasRichText(text: string): boolean {
  return /<b>/i.test(text) || /<br\s*\/?>/i.test(text);
}

function getRichTextRaw(data: CellHookData): string | undefined {
  const raw = data.cell.raw;
  return typeof raw === 'string' && hasRichText(raw) ? raw : undefined;
}

function buildWrappedRichTextLines(
  raw: string,
  maxWidth: number,
  pdf: jsPDF,
  fontFamily: string
): RichTextLine[] {
  const lines: RichTextLine[] = [];

  for (const logicalLine of splitRichTextLines(raw)) {
    const richTextLine = parseRichTextLine(logicalLine);

    if (!richTextLine.text) {
      lines.push(richTextLine);
      continue;
    }

    pdf.setFont(fontFamily, richTextLine.fontStyle);
    const wrappedLines =
      maxWidth > 0
        ? (pdf.splitTextToSize(richTextLine.text, maxWidth) as string[])
        : [richTextLine.text];

    for (const wrappedLine of wrappedLines) {
      lines.push({text: wrappedLine, fontStyle: richTextLine.fontStyle});
    }
  }

  pdf.setFont(fontFamily, 'normal');

  return lines.length > 0 ? lines : [{text: '', fontStyle: 'normal'}];
}

function getWrappedPlainTextLines(
  raw: string,
  maxWidth: number,
  pdf: jsPDF,
  fontFamily: string
): string[] {
  return buildWrappedRichTextLines(raw, maxWidth, pdf, fontFamily).map((line) => line.text);
}

function splitRichTextLines(raw: string): string[] {
  return raw.replace(/<br\s*\/?>/gi, '\n').split('\n');
}

function parseRichTextLine(line: string): RichTextLine {
  const trimmedLine = line.trim();
  const boldMatch = trimmedLine.match(/^<b>(.*)<\/b>$/i);

  return {
    text: (boldMatch ? boldMatch[1] : trimmedLine).trim(),
    fontStyle: boldMatch ? 'bold' : 'normal',
  };
}

function getVerticalOffset(
  valign: VAlignType,
  availableHeight: number,
  contentHeight: number
): number {
  const extraHeight = Math.max(availableHeight - contentHeight, 0);

  if (valign === 'bottom') {
    return extraHeight;
  }

  if (valign === 'middle') {
    return extraHeight / 2;
  }

  return 0;
}
